// The `/_session-bridge` answer — ONE definition for every bridge surface: the
// workspace shell's DOMQL page (workspace pages/session-bridge.js), the
// canvas's (canvas pages/session-bridge.js) and the light bridge entry
// (workspace bridge.html → bridge/index.js), which answers in a fraction of
// the time because it boots nothing but this.
//
// The rules are the bridge's own and unchanged: only an exact same-channel
// peer (sessionBridgePeers.js) is answered and only a
// `symbols-session-request` — both checked before anything is read; storage
// access is asked first (a partitioned frame); the answer is the ACCESS token
// and its expiry, never the refresh token (sessionBridgeTokens.js); no session
// → `{ ok: false, error: 'no-session' }`. The reply goes to the asking frame's
// origin only.
//
// The two rules are imported from their own modules; both are in the
// functions bag, so the imports also resolve on a published surface
// (functions/index.js, as for sdkClient.js / bootResolve.js).
import { isSessionBridgePeer } from './sessionBridgePeers.js'
import { collectBridgeTokens } from './sessionBridgeTokens.js'

// answerSessionBridgeRequest(ev, { getSdk })
//   ev       the window `message` event
//   getSdk   returns this origin's SDK (or a promise of it); called only when
//            the owner's token must rotate first — a DOMQL page passes
//            `() => el.call('getSDK')`, the light entry a lazy import
// Resolves true when it answered, false when the message was not a request
// from a peer.
export const answerSessionBridgeRequest = async (ev, { getSdk } = {}) => {
  if (!ev || !isSessionBridgePeer(ev.origin)) return false
  if (!ev.data || ev.data.type !== 'symbols-session-request') return false
  const reply = (payload) =>
    ev.source && ev.source.postMessage({ type: 'symbols-session-response', ...payload }, ev.origin)

  // Chrome's Third-Party Storage Partitioning gives a cross-site frame an
  // empty, isolated localStorage by default. requestStorageAccess() promotes
  // it to first-party storage when Chrome's heuristics approve (granted when
  // the person recently visited this host as a top-level page), so the live
  // tokens can be read.
  try {
    if (typeof document !== 'undefined' && typeof document.requestStorageAccess === 'function') {
      await document.requestStorageAccess()
    }
  } catch {
    // Refused — read the partitioned store. Worst case the answer is
    // `no-session` and the peer shows its own sign-in hint.
  }

  const sdkTokens = (await collectBridgeTokens({ getSdk })) || {}
  if (!sdkTokens.symbols_access_token) {
    reply({ ok: false, error: 'no-session' })
    return true
  }
  reply({ ok: true, sdkTokens })
  return true
}

// Tell the framing page this bridge is listening: the asker waits for it
// before it posts its request (and retries on its own besides). Call it only
// once the `message` listener is armed.
export const announceSessionBridgeReady = () => {
  try {
    if (typeof window !== 'undefined' && window.parent && window.parent !== window) {
      window.parent.postMessage({ type: 'symbols-session-ready' }, '*')
    }
  } catch {
    /* a detached frame — the asker's own retries cover it */
  }
}
